<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getTranslatedProperty, getProductUrl } from "@shopware/helpers";
import { formatPrice } from "~/helpers/formatters";
import { getThumbnailUrl } from "~/helpers/mediaHelpers";
import { getExclusiveDeliveryMethodMessage } from "~/helpers/shippingMethodHelpers";

interface Props {
	product: Schemas["Product"];
	renderMode?: "single" | "double";
}

const { product, renderMode = "single" } = defineProps<Props>();

const img = useImage();
</script>

<template>
	<NuxtLink :to="getProductUrl(product)" class="group">
		<KippieProductCard
			class="h-full"
			:class="{
				'group-odd:rounded-l-none group-even:rounded-r-none md:odd:rounded-l-3xl md:even:rounded-r-3xl':
					renderMode === 'double'
			}"
			hide-favourite
		>
			<template #image v-if="!!product.cover?.media">
				<NuxtImg
					class="h-full mx-auto"
					:src="getThumbnailUrl(product.cover.media)"
					:alt="getTranslatedProperty(product, 'name')"
					height="185"
					loading="lazy"
					format="webp"
					:quality="90"
					placeholder
				/>
			</template>
			<template #labels>
				<KippieLabel
					v-if="!!product.customFields?.kippie_product_fields_first_label"
					:label="product.customFields?.kippie_product_fields_first_label"
				/>
				<KippieLabel
					v-if="!!product.customFields?.kippie_product_fields_second_label"
					:label="product.customFields?.kippie_product_fields_second_label"
				/>
			</template>
			<template v-if="!!product.customFields?.kippie_product_fields_main_label" #action>
				<p class="px-4 py-[5px] rounded-r-full w-fit text-2xs bg-red text-white">
					{{ product.customFields?.kippie_product_fields_main_label }}
				</p>
			</template>
			<div class="flex flex-col grow">
				<Typography variant="p" class="text-base font-bold">
					{{ getTranslatedProperty(product, "name") }}
				</Typography>
				<Typography variant="p" class="text-xs text-gray">
					{{ getExclusiveDeliveryMethodMessage(product) }}
				</Typography>
				<Typography variant="p" class="text-sm">
					{{ product.customFields?.kippie_product_fields_short_description }}
				</Typography>
			</div>

			<span class="font-medium mt-3 md:hidden" v-if="renderMode === 'double'">
				<span class="flex items-center gap-2">
					<div class="text-sm font-medium line-through text-gray" v-if="product.calculatedPrice.listPrice">
						{{ formatPrice(product.calculatedPrice.listPrice?.price) }}
					</div>
					{{ formatPrice(product.calculatedPrice.totalPrice) }}
				</span>
			</span>

			<AddToCartButton :product-prop="product" v-slot="{ onAddToCart }">
				<KippieButton
					block
					:justify="renderMode === 'single' ? 'space-between' : 'center'"
					class="mt-3 flex-none"
					@click.stop="onAddToCart"
					fake
				>
					Voeg toe
					<template #right v-if="renderMode === 'single'">
						<span class="flex items-center gap-2">
							<div
								class="text-sm font-medium line-through text-gray"
								v-if="product.calculatedPrice.listPrice"
							>
								{{ formatPrice(product.calculatedPrice.listPrice?.price) }}
							</div>
							{{ formatPrice(product.calculatedPrice.totalPrice) }}
						</span>
					</template>
				</KippieButton>
			</AddToCartButton>
		</KippieProductCard>
	</NuxtLink>
</template>
